import React from 'react';
import { Link } from 'react-router-dom';
import { User, BusinessInformation, InstagramSettings, MessageTemplate, AnalyticsData, Update } from '../types';

interface DashboardProps {
  instagramSettings: InstagramSettings;
  templates: MessageTemplate[];
  businessInfo: BusinessInformation;
  analyticsData: AnalyticsData;
  updates: Update[];
  user: User;
  onInstagramSettingsUpdate: (settings: InstagramSettings) => void;
  onTemplateUpdate: (template: MessageTemplate) => void;
  onBusinessInfoUpdate: (info: BusinessInformation) => void;
  onAnalyticsUpdate: (data: AnalyticsData) => void;
  onUpdateMarkAsRead: (updateId: string) => void;
  onUserUpdate: (userData: Partial<User>) => void;
}

export const Dashboard: React.FC<DashboardProps> = ({
  instagramSettings,
  templates,
  businessInfo,
  analyticsData,
  updates,
  user,
  onInstagramSettingsUpdate,
  onTemplateUpdate,
  onBusinessInfoUpdate,
  onAnalyticsUpdate,
  onUpdateMarkAsRead,
  onUserUpdate
}) => {
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Business Information</h2>
          <p className="text-gray-600 mb-2">Type: {businessInfo.businessType}</p>
          <p className="text-gray-600 mb-2">Industry: {businessInfo.industry}</p>
          <p className="text-gray-600">Target Audience: {businessInfo.targetAudience}</p>
          <Link to="/business" className="mt-4 inline-block text-blue-600 hover:text-blue-800">
            Edit Business Info
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Instagram Settings</h2>
          <p className="text-gray-600 mb-2">Auto Reply: {instagramSettings.autoReply ? 'Enabled' : 'Disabled'}</p>
          <p className="text-gray-600 mb-2">Reply Delay: {instagramSettings.replyDelay} minutes</p>
          <p className="text-gray-600">Max Replies Per Day: {instagramSettings.maxRepliesPerDay}</p>
          <Link to="/dashboard" className="mt-4 inline-block text-blue-600 hover:text-blue-800">
            Edit Instagram Settings
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Message Templates</h2>
          <p className="text-gray-600 mb-4">Total Templates: {templates.length}</p>
          <Link to="/templates" className="inline-block text-blue-600 hover:text-blue-800">
            Manage Templates
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Analytics Overview</h2>
          <p className="text-gray-600 mb-2">Total Messages: {analyticsData.totalMessages}</p>
          <p className="text-gray-600 mb-2">Total Responses: {analyticsData.totalResponses}</p>
          <p className="text-gray-600">Response Rate: {analyticsData.responseRate}%</p>
          <Link to="/analytics" className="mt-4 inline-block text-blue-600 hover:text-blue-800">
            View Full Analytics
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Recent Updates</h2>
          {updates.slice(0, 3).map(update => (
            <div key={update.id} className="mb-4">
              <p className="text-gray-600">{update.title}</p>
              <p className="text-sm text-gray-500">{new Date(update.createdAt).toLocaleDateString()}</p>
            </div>
          ))}
          <Link to="/updates" className="inline-block text-blue-600 hover:text-blue-800">
            View All Updates
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Account Details</h2>
          <p className="text-gray-600 mb-2">Name: {user.name}</p>
          <p className="text-gray-600 mb-2">Email: {user.email}</p>
          <p className="text-gray-600">Plan: {user.plan}</p>
          <Link to="/account" className="mt-4 inline-block text-blue-600 hover:text-blue-800">
            Edit Account
          </Link>
        </div>
      </div>
    </div>
  );
}; 